"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { editTutorProfile } from "../../../api/editProfile";
import { getSubjectsSectionSchema } from "../../../schemas/profileEditSchemas";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore";
import { cn } from "@/lib/utils";
import { API_ENDPOINTS } from "@/constants/api";
import { axiosApiInstance } from "@/libs/axios";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";
import { Badge } from "@/components/ui/badge";
import { InformationTooltip } from "@/components/InformationTooltip";
import { useProfileEditFooterStore } from "@/features/profile/tutor/store/useProfileEditFooterStore";

interface SubjectsEditProps {
  onCancel: () => void;
  onSave: () => void;
}

interface CurriculumLevel {
  id: string;
  name: string;
  description?: string;
}

interface Subject {
  id: string;
  name: string;
  icon: string;
  is_group_subject: boolean;
  curriculum_levels?: CurriculumLevel[];
}

interface GroupedSubject extends Subject {
  children: Subject[];
  curriculum_levels: CurriculumLevel[];
}

interface SubjectResponse {
  data: (Subject | GroupedSubject)[];
}

interface SubjectData {
  id: string;
  curriculum_levels: string[];
}

// Enum for view states
enum ViewState {
  CURRENT_SUBJECTS = 0,
  ALL_SUBJECTS = 1,
  CURRICULUM_SELECTION = 2,
}

export function SubjectsEdit({ onCancel, onSave }: SubjectsEditProps) {
  const { profileData, setToReload } = useProfileDataStore();
  const { t } = useTranslation(["profile", "common"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });
  const [subjects, setSubjects] = useState<(Subject | GroupedSubject)[]>([]);
  const [flattenedSubjects, setFlattenedSubjects] = useState<Subject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentView, setCurrentView] = useState<ViewState>(ViewState.CURRENT_SUBJECTS);
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [selectedCurriculumLevels, setSelectedCurriculumLevels] = useState<string[]>([]);

  // Local state to track changes before saving
  const [localSubjects, setLocalSubjects] = useState<SubjectData[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const setFooter = useProfileEditFooterStore((s) => s.setFooter);

  const form = useForm({
    resolver: zodResolver(getSubjectsSectionSchema(t)),
    defaultValues: {
      subjects: [],
    },
  });

  // Initialize local subjects from profile data
  useEffect(() => {
    const initialSubjects =
      profileData?.subjects_section?.map((subject) => ({
        id: subject.subject.id,
        curriculum_levels: subject.curriculumLevels.map((level) => level.id),
      })) || [];

    setLocalSubjects(initialSubjects);
  }, [profileData]);

  useEffect(() => {
    getSubjects();
  }, []);

  // Flatten subjects for the all subjects view
  useEffect(() => {
    const flattened: Subject[] = [];

    subjects.forEach((subject) => {
      if ("children" in subject) {
        // Add parent subject
        flattened.push({
          id: subject.id,
          name: subject.name,
          icon: subject.icon,
          is_group_subject: subject.is_group_subject,
          curriculum_levels: subject.curriculum_levels,
        });

        // Add children with inherited curriculum levels from parent
        subject.children.forEach((child) => {
          flattened.push({
            ...child,
            curriculum_levels: subject.curriculum_levels, // Children inherit from parent
          });
        });
      } else {
        flattened.push(subject);
      }
    });

    setFlattenedSubjects(flattened);
  }, [subjects]);

  async function getSubjects() {
    try {
      setIsLoading(true);
      const { data } = await axiosApiInstance.get<SubjectResponse>(
        API_ENDPOINTS.TUTOR.UTILS.SUBJECTS,
        {
          params: { locale_code: profileData?.header_section.locale_code, search: "" },
        },
      );

      setSubjects(data.data);
    } catch (error) {
      console.error(error);
      toast.error(t("common:error"), {
        description: t("common:errorMessage"),
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleSubmit = async () => {
    try {
      if (profileData) {
        // Only make API call when actually saving
        await editTutorProfile(profileData.id, "subjects", { subjects: localSubjects });
        toast.success(t("common:success"), {
          description: t("common:successMessage"),
        });
        onSave();
        setToReload(true);
      }
    } catch (error) {
      console.error(error);
      toast.error(t("common:error"), {
        description: t("common:errorMessage"),
      });
    }
  };

  const handleCurriculumLevelsChange = (subjectId: string, newLevels: string[]) => {
    setLocalSubjects((prev) => {
      const updated = [...prev];

      if (newLevels.length === 0) {
        // Remove subject if no curriculum levels selected
        return updated.filter((s) => s.id !== subjectId);
      } else {
        const existingIndex = updated.findIndex((s) => s.id === subjectId);

        if (existingIndex !== -1) {
          updated[existingIndex].curriculum_levels = newLevels;
        } else {
          updated.push({
            id: subjectId,
            curriculum_levels: newLevels,
          });
        }

        return updated;
      }
    });
    setHasUnsavedChanges(true);
  };

  const isSubjectSelected = (id: string) => localSubjects.some((subject) => subject.id === id);

  const getSelectedLevels = (subjectId: string) =>
    localSubjects.find((s) => s.id === subjectId)?.curriculum_levels || [];

  const getSubjectById = (id: string): Subject | undefined => {
    // First check in flattened subjects
    const subject = flattenedSubjects.find((s) => s.id === id);

    if (subject) return subject;

    // If not found, check in original subjects (including nested)
    for (const s of subjects) {
      if (s.id === id) return s;
      if ("children" in s) {
        const child = s.children.find((c) => c.id === id);

        if (child) {
          // Return child with parent's curriculum levels
          return {
            ...child,
            curriculum_levels: s.curriculum_levels,
          };
        }
      }
    }

    return undefined;
  };

  const handleEditCurriculumLevels = (subject: Subject) => {
    setSelectedSubject(subject);
    setSelectedCurriculumLevels(getSelectedLevels(subject.id));
    setCurrentView(ViewState.CURRICULUM_SELECTION);
  };

  const handleCurriculumLevelToggle = (levelId: string) => {
    if (selectedCurriculumLevels.includes(levelId)) {
      setSelectedCurriculumLevels(selectedCurriculumLevels.filter((id) => id !== levelId));
    } else {
      setSelectedCurriculumLevels([...selectedCurriculumLevels, levelId]);
    }
  };

  const handleCurriculumLevelConfirm = () => {
    if (selectedSubject) {
      handleCurriculumLevelsChange(selectedSubject.id, selectedCurriculumLevels);
      setCurrentView(ViewState.CURRENT_SUBJECTS);
      setSelectedSubject(null);
    }
  };

  const handleSubjectSelect = (subject: Subject) => {
    setSelectedSubject(subject);
    setSelectedCurriculumLevels(getSelectedLevels(subject.id));
    setCurrentView(ViewState.CURRICULUM_SELECTION);
  };

  const handleRemoveSubject = (subjectId: string) => {
    setLocalSubjects((prev) => prev.filter((s) => s.id !== subjectId));
    setHasUnsavedChanges(true);
  };

  const navigateBack = () => {
    if (currentView === ViewState.ALL_SUBJECTS) {
      setCurrentView(ViewState.CURRENT_SUBJECTS);
    } else if (currentView === ViewState.CURRICULUM_SELECTION) {
      if (isSubjectSelected(selectedSubject?.id || "")) {
        setCurrentView(ViewState.CURRENT_SUBJECTS);
      } else {
        setCurrentView(ViewState.ALL_SUBJECTS);
      }
      setSelectedSubject(null);
    }
  };

  const handleCancel = () => {
    // Reset local changes and close dialog
    const initialSubjects =
      profileData?.subjects_section?.map((subject) => ({
        id: subject.subject.id,
        curriculum_levels: subject.curriculumLevels.map((level) => level.id),
      })) || [];

    setLocalSubjects(initialSubjects);
    setHasUnsavedChanges(false);
    onCancel();
  };

  const getSelectedCurriculumLevelsNames = (subjectId: string) => {
    const subject = localSubjects.find((s) => s.id === subjectId);

    if (!subject) return [];

    // Get curriculum levels from the subject data
    const subjectData = getSubjectById(subjectId);
    const curriculumLevels = subjectData?.curriculum_levels || [];

    return subject.curriculum_levels
      .map((levelId) => {
        const level = curriculumLevels.find((l) => l.id === levelId);

        return level ? level.name : "";
      })
      .filter(Boolean);
  };

  // Filter subjects based on search term
  const filteredSubjects = flattenedSubjects.filter(
    (subject) => search.length === 0 || subject.name.toLowerCase().includes(search.toLowerCase()),
  );

  // Page transitions with opacity only
  const pageTransitionVariants = {
    initial: {
      opacity: 0,
    },
    animate: {
      opacity: 1,
      transition: { duration: 0.2, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.15, ease: "easeIn" },
    },
  };

  // Staggered item animations
  const listItemVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: "easeOut",
        delay: i * 0.02,
      },
    }),
  };

  // Render current subjects view
  const renderCurrentSubjectsView = () => {
    return (
      <motion.div
        key="current-subjects"
        animate="animate"
        className="flex h-full flex-col"
        exit="exit"
        initial="initial"
        variants={pageTransitionVariants}
      >
        <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-12 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-indigo-400 to-cyan-500 opacity-10" />
          <div className="pointer-events-none absolute -bottom-10 -left-12 h-28 w-28 rounded-full bg-gradient-to-tr from-amber-400 to-pink-500 opacity-10" />
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2 max-sm:justify-evenly">
              <div className="flex items-center gap-2">
                <div className="bg-background/60 flex items-center rounded-xl p-2">
                  <Icon
                    className="text-accent-foreground"
                    height="1.125rem"
                    icon="mdi:bookshelf"
                    width="1.125rem"
                  />
                </div>
                <CardTitle className="text-accent-foreground text-lg">
                  {t("profile:selectSubjects.subjects")}
                </CardTitle>
              </div>
              <Button
                type="button"
                variant="foreground"
                onClick={() => {
                  setCurrentView(ViewState.ALL_SUBJECTS);
                  setSearch("");
                }}
              >
                <Icon className="sm:mr-2" height="1rem" icon="mdi:plus" width="1rem" />
                <span className={"max-sm:hidden"}>{t("profile:selectSubjects.addSubject")}</span>
              </Button>
            </div>
            <CardDescription className="text-muted-foreground">
              {t("profile:selectSubjects.subtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-24">
            {localSubjects.length > 0 ? (
              <div className="space-y-3">
                {localSubjects.map((subjectData, index) => {
                  const subject = getSubjectById(subjectData.id);

                  if (!subject) return null;

                  const curriculumNames = getSelectedCurriculumLevelsNames(subject.id);

                  return (
                    <motion.div
                      key={subject.id}
                      animate="visible"
                      custom={index}
                      initial="hidden"
                      variants={listItemVariants}
                    >
                      <Card className="bg-muted/60 border-border/30 hover:border-accent/30 hover:bg-accent/50 cursor-pointer transition-all">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex flex-1 items-start space-x-3">
                              <div className="bg-accent-foreground/10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full max-[350px]:hidden">
                                <Icon
                                  className="text-accent-foreground"
                                  height="1.25rem"
                                  icon={subject.icon}
                                  width="1.25rem"
                                />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h4 className="text-accent-foreground mb-2 text-base font-medium">
                                  {subject.name}
                                </h4>
                                <div className="flex flex-wrap gap-1">
                                  {curriculumNames.slice(0, 3).map((name, idx) => (
                                    <Badge
                                      key={idx}
                                      className="text-muted-foreground text-xs"
                                      variant="outline"
                                    >
                                      {name}
                                    </Badge>
                                  ))}
                                  {curriculumNames.length > 3 && (
                                    <Badge
                                      className="text-muted-foreground text-xs"
                                      variant="outline"
                                    >
                                      +{curriculumNames.length - 3}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="ml-2 flex space-x-1">
                              <Button
                                className="h-8 w-8"
                                size="icon"
                                type="button"
                                variant="ghost"
                                onClick={() => handleEditCurriculumLevels(subject)}
                              >
                                <Icon
                                  className="text-accent-foreground"
                                  height="1rem"
                                  icon="tabler:edit"
                                  width="1rem"
                                />
                              </Button>
                              <Button
                                className="hover:bg-destructive/10 hover:text-destructive h-8 w-8"
                                size="icon"
                                type="button"
                                variant="ghost"
                                onClick={() => handleRemoveSubject(subject.id)}
                              >
                                <Icon
                                  className="text-accent-foreground"
                                  height="1rem"
                                  icon="mdi:trash-outline"
                                  width="1rem"
                                />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <motion.div
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-12"
                initial={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-muted/50 mb-4 rounded-full p-6">
                  <Icon
                    className="text-muted-foreground"
                    height="2rem"
                    icon="tabler:book-off"
                    width="2rem"
                  />
                </div>
                <h4 className="text-accent-foreground mb-2 font-medium">
                  {t("profile:selectSubjects.noSubjectsAvailable")}
                </h4>
                <p className="text-muted-foreground max-w-sm text-center text-sm">
                  {t("profile:selectSubjects.subtitle")}
                </p>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  // Render all subjects view
  const renderAllSubjectsView = () => {
    return (
      <motion.div
        key="all-subjects"
        animate="animate"
        className="flex h-full flex-col"
        exit="exit"
        initial="initial"
        variants={pageTransitionVariants}
      >
        <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-10 -right-14 h-36 w-36 rounded-full bg-gradient-to-tr from-purple-500 to-pink-500 opacity-10" />
          <div className="pointer-events-none absolute -bottom-8 -left-10 h-28 w-28 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 opacity-10" />
          <CardHeader className="pb-2">
            <div className="mb-2 flex items-center gap-2">
              <Button size="sm" type="button" variant="outline" onClick={navigateBack}>
                <Icon className="mr-1" height="1rem" icon="mdi:arrow-left" width="1rem" />
                {t("common:back")}
              </Button>
              <CardTitle className="text-accent-foreground text-lg">
                {t("profile:selectSubjects.searchSubjects")}
              </CardTitle>
              <InformationTooltip description={t("profile:selectSubjects.subtitle")} />
            </div>
            <CardDescription className="text-muted-foreground">
              {t("profile:selectSubjects.subtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-24">
            <div className="relative mb-4">
              <div className="text-accent-foreground/60 absolute top-3 left-3">
                <Icon height="1rem" icon="mdi:magnify" width="1rem" />
              </div>
              <Input
                className="pr-10 pl-9"
                placeholder={t("profile:selectSubjects.searchSubjects")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search.length > 0 && (
                <Button
                  className="absolute inset-y-0 right-1 my-auto h-8 w-8"
                  size="icon"
                  type="button"
                  variant="ghost"
                  onClick={() => setSearch("")}
                >
                  <Icon height="1rem" icon="mdi:close" width="1rem" />
                </Button>
              )}
            </div>

            {isLoading ? (
              <div className="flex h-[200px] w-full items-center justify-center">
                <motion.div
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center gap-3"
                  initial={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="border-accent-foreground h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
                  <span className="text-muted-foreground text-sm">
                    {t("profile:selectSubjects.loadingSubjects")}
                  </span>
                </motion.div>
              </div>
            ) : filteredSubjects.length > 0 ? (
              <div className="space-y-2 pr-1">
                {filteredSubjects.map((subject, index) => {
                  const isSelected = isSubjectSelected(subject.id);

                  return (
                    <motion.div
                      key={subject.id}
                      animate="visible"
                      custom={index}
                      initial="hidden"
                      variants={listItemVariants}
                    >
                      <Card
                        className={cn(
                          "cursor-pointer overflow-hidden border-none transition-all duration-200",
                          "hover:bg-sidebar-accent",
                          isSelected ? "bg-side-accent" : "bg-card/80",
                        )}
                        onClick={() => handleSubjectSelect(subject)}
                      >
                        <CardContent className="p-3">
                          <div className="flex max-w-full items-center justify-between">
                            <div className="flex max-w-[80%] items-center space-x-3">
                              <div className="bg-accent-foreground/10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full max-[350px]:hidden">
                                <Icon
                                  className="text-accent-foreground"
                                  height="1.25rem"
                                  icon={subject.icon}
                                  width="1.25rem"
                                />
                              </div>
                              <div className={"max-w-full"}>
                                <h4 className="text-muted-foreground font-medium">
                                  {subject.name}
                                </h4>
                                {isSelected && (
                                  <div className="text-muted-foreground mt-1 max-w-full truncate text-xs">
                                    {getSelectedCurriculumLevelsNames(subject.id).join(", ")}
                                  </div>
                                )}
                              </div>
                            </div>
                            {isSelected && (
                              <motion.div
                                animate={{ scale: 1, opacity: 1 }}
                                initial={{ scale: 0.8, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                <Icon
                                  className="text-accent-foreground"
                                  height="1.25rem"
                                  icon="mdi:check"
                                  width="1.25rem"
                                />
                              </motion.div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <motion.div
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-12"
                initial={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Icon
                  className="text-muted-foreground mb-2"
                  height={40}
                  icon="tabler:search-off"
                  width={40}
                />
                <span className="text-muted-foreground text-center">
                  {search.length > 0
                    ? t("profile:selectSubjects.noMatchingSubjects")
                    : t("profile:selectSubjects.noSubjectsAvailable")}
                </span>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  // Render curriculum selection view
  const renderCurriculumSelectionView = () => {
    if (!selectedSubject) return null;

    const curriculumLevels = selectedSubject.curriculum_levels || [];

    return (
      <motion.div
        key="curriculum-selection"
        animate="animate"
        className="flex h-full flex-col"
        exit="exit"
        initial="initial"
        variants={pageTransitionVariants}
      >
        <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-8 -right-10 h-24 w-24 rounded-full bg-gradient-to-br from-amber-400 to-pink-500 opacity-10" />
          <div className="pointer-events-none absolute -bottom-10 -left-12 h-28 w-28 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 opacity-10" />
          <CardHeader className="pb-2">
            <div className="mb-2 flex items-center space-x-3">
              <Button size="sm" type="button" variant="outline" onClick={navigateBack}>
                <Icon className="mr-1" height="1rem" icon="mdi:arrow-left" width="1rem" />
                {t("common:back")}
              </Button>
              <CardTitle className="text-accent-foreground text-lg">
                {selectedSubject.name}
              </CardTitle>
            </div>
            <CardDescription className="text-muted-foreground">
              {t("profile:selectSubjects.selectCurriculumLevels", {
                subject: selectedSubject.name,
              })}
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-24">
            <div className="grid gap-2">
              {curriculumLevels.map((level, index) => (
                <motion.div
                  key={level.id}
                  animate="visible"
                  custom={index}
                  initial="hidden"
                  variants={listItemVariants}
                >
                  <Button
                    className={cn(
                      "h-auto w-full justify-start border-none px-4 py-3 text-start",
                      selectedCurriculumLevels.includes(level.id)
                        ? "bg-side-accent text-accent-foreground hover:bg-side-accent/90"
                        : "bg-card text-muted-foreground hover:bg-muted/50",
                    )}
                    type="button"
                    variant="outline"
                    onClick={() => handleCurriculumLevelToggle(level.id)}
                  >
                    <div className="flex w-full items-center justify-between">
                      <span className="truncate">{level.name}</span>
                      {selectedCurriculumLevels.includes(level.id) && (
                        <motion.div
                          animate={{ scale: 1, opacity: 1 }}
                          initial={{ scale: 0, opacity: 0 }}
                          transition={{ duration: 0.2, type: "spring", stiffness: 500 }}
                        >
                          <Icon
                            className="text-accent-foreground ml-2 shrink-0"
                            height={16}
                            icon="mdi:check"
                            width={16}
                          />
                        </motion.div>
                      )}
                    </div>
                  </Button>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  // Render action buttons based on current view
  const renderActionButtons = () => {
    if (currentView === ViewState.CURRENT_SUBJECTS) {
      return (
        <>
          <Button
            className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
            type="button"
            variant="outline"
            onClick={handleCancel}
          >
            {t("common:cancel")}
          </Button>
          <Button
            className="bg-accent-foreground/90 hover:bg-accent-foreground text-background rounded-xl"
            disabled={!hasUnsavedChanges}
            onClick={handleSubmit}
          >
            {t("common:save")}
          </Button>
        </>
      );
    } else if (currentView === ViewState.ALL_SUBJECTS) {
      return (
        <>
          <Button
            className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
            type="button"
            variant="outline"
            onClick={navigateBack}
          >
            {t("common:cancel")}
          </Button>
          <Button
            className="bg-accent-foreground/90 hover:bg-accent-foreground text-background rounded-xl"
            type="button"
            onClick={() => {
              setCurrentView(ViewState.CURRENT_SUBJECTS);
            }}
          >
            {t("common:done")}
          </Button>
        </>
      );
    } else if (currentView === ViewState.CURRICULUM_SELECTION) {
      return (
        <>
          <Button
            className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
            type="button"
            variant="outline"
            onClick={navigateBack}
          >
            {t("common:cancel")}
          </Button>
          <Button
            className="bg-accent-foreground/90 hover:bg-accent-foreground text-background rounded-xl"
            disabled={selectedCurriculumLevels.length === 0}
            type="button"
            onClick={handleCurriculumLevelConfirm}
          >
            {t("common:confirm")}
          </Button>
        </>
      );
    }

    return null;
  };

  // Register persistent footer content for this section
  useEffect(() => {
    const footerNode = <div className="flex w-full justify-end gap-3">{renderActionButtons()}</div>;

    setFooter({ key: `subjects-${currentView}`, node: footerNode });

    return () => {
      // Keep stable for quick tab/view changes
    };
  }, [currentView, hasUnsavedChanges, selectedCurriculumLevels, t]);

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <Form {...form}>
        <div className="flex h-full flex-col">
          <FormField
            control={form.control}
            name="subjects"
            render={() => (
              <FormItem className="flex-1 overflow-hidden">
                <FormControl>
                  <div className="h-full">
                    <AnimatePresence mode="wait">
                      {currentView === ViewState.CURRENT_SUBJECTS && renderCurrentSubjectsView()}
                      {currentView === ViewState.ALL_SUBJECTS && renderAllSubjectsView()}
                      {currentView === ViewState.CURRICULUM_SELECTION &&
                        renderCurriculumSelectionView()}
                    </AnimatePresence>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Footer handled by persistent footer */}
        </div>
      </Form>
    </motion.div>
  );
}
